import React, { useEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'

export interface TailwindModalProps {
  open: boolean
  onClose?: () => void
  children: React.ReactNode
  /** If false, clicking the backdrop does not call onClose (default true) */
  closeOnBackdropClick?: boolean
  disableEscapeKeyDown?: boolean
  /** Extra classes on the backdrop overlay */
  backdropClassName?: string
  /** Inline styles on the backdrop overlay */
  backdropStyle?: React.CSSProperties
  /** aria-labelledby */
  'aria-labelledby'?: string
  /** aria-describedby */
  'aria-describedby'?: string
  /** Extra className on the root portal wrapper */
  className?: string
  /** z-index override (default 1300 to match former MUI Modal) */
  zIndex?: number
  /**
   * Keep children mounted for this many ms after `open` flips false, so a CSS
   * exit animation can play. 0 (default) unmounts immediately.
   * The backdrop always carries data-state="open"|"closed" for CSS to target.
   */
  exitMs?: number
  /** slotProps-like: extra props for the backdrop element */
  slotProps?: {
    backdrop?: {
      onTouchMove?: (e: React.TouchEvent) => void
      style?: React.CSSProperties
    }
  }
}

const TailwindModal: React.FC<TailwindModalProps> = ({
  open,
  onClose,
  children,
  closeOnBackdropClick = true,
  disableEscapeKeyDown = false,
  backdropClassName = '',
  backdropStyle,
  className = '',
  zIndex = 1300,
  exitMs = 0,
  slotProps,
  ...ariaProps
}) => {
  const overlayRef = useRef<HTMLDivElement>(null)
  const previousFocus = useRef<HTMLElement | null>(null)
  const wasOpen = useRef(open)
  const [exiting, setExiting] = useState(false)

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !disableEscapeKeyDown) {
        onClose?.()
      }
    },
    [disableEscapeKeyDown, onClose],
  )

  // Hold the tree mounted through the exit animation, then drop it.
  useEffect(() => {
    if (open) {
      setExiting(false)
      return
    }

    // Never opened, or caller opted out of an exit animation: unmount at once.
    if (!wasOpen.current || exitMs <= 0) return

    setExiting(true)
    const timer = setTimeout(() => setExiting(false), exitMs)

    return () => clearTimeout(timer)
  }, [open, exitMs])

  // Declared after the effect above so it reads the previous render's value.
  useEffect(() => {
    wasOpen.current = open
  })

  /*
   * `exiting` is set from an effect, which React runs *after* the render where
   * `open` flipped false — so on that one frame it is still false. Deriving the
   * closing frame here too keeps the node mounted continuously; relying on
   * `exiting` alone would unmount and immediately remount it, causing a blink.
   */
  const closing = !open && wasOpen.current && exitMs > 0
  const shouldRender = open || exiting || closing

  useEffect(() => {
    if (!shouldRender) return
    previousFocus.current = document.activeElement as HTMLElement
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = prevOverflow
      previousFocus.current?.focus()
    }
  }, [shouldRender])

  // Escape only closes while genuinely open, not while animating out.
  useEffect(() => {
    if (!open) return
    document.addEventListener('keydown', handleEscape)

    return () => document.removeEventListener('keydown', handleEscape)
  }, [open, handleEscape])

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdropClick && open && e.target === overlayRef.current) {
      onClose?.()
    }
  }

  if (!shouldRender) return null

  const backdropSlotStyle = slotProps?.backdrop?.style
  const backdropSlotTouchMove = slotProps?.backdrop?.onTouchMove

  return createPortal(
    <div
      ref={overlayRef}
      role="presentation"
      data-state={open ? 'open' : 'closed'}
      className={`fixed inset-0 flex items-center justify-center ${backdropClassName} ${className}`}
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        ...backdropSlotStyle,
        ...backdropStyle,
        // Apply last so backdropStyle cannot wipe z-index with `undefined`
        zIndex,
        // Swallow no clicks while the modal is animating away.
        ...(open ? null : { pointerEvents: 'none' as const }),
      }}
      onClick={handleBackdropClick}
      onTouchMove={backdropSlotTouchMove}
      aria-labelledby={ariaProps['aria-labelledby']}
      aria-describedby={ariaProps['aria-describedby']}
    >
      {children}
    </div>,
    document.body,
  )
}

export default TailwindModal
